import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Lucide from 'lucide-react';
import { codexApi } from '../api';
import { celebrate } from '../../lib/celebrate';

const SLOT_META = {
  robe:        { label: 'Robes',         icon: 'Shirt',     blurb: 'Cloaks worn by the faithful — for journey, for sanctuary.' },
  accessory:   { label: 'Accessories',   icon: 'Wand',      blurb: 'Symbols carried in the hand or worn close to the heart.' },
  scroll_skin: { label: 'Scroll Skins',  icon: 'Scroll',    blurb: 'Adorn your Daily Scroll with sacred parchment styles.' },
  frame:       { label: 'Profile Frames', icon: 'Square',   blurb: 'Forged borders that surround your portrait.' },
  title:       { label: 'Titles',        icon: 'Crown',     blurb: 'A new name to wear under your level.' },
};

const TIER_RING = {
  common:    { ring: 'rgba(212,175,55,0.4)',  glow: '0 0 18px rgba(212,175,55,0.25)' },
  rare:      { ring: 'rgba(168,85,247,0.55)', glow: '0 0 22px rgba(168,85,247,0.4)' },
  epic:      { ring: 'rgba(234,88,12,0.6)',   glow: '0 0 26px rgba(234,88,12,0.45)' },
  legendary: { ring: 'rgba(245,217,124,0.85)', glow: '0 0 36px rgba(245,217,124,0.65), 0 0 80px rgba(168,85,247,0.4)' },
};

function formatShekels(n) {
  return n.toLocaleString('en-GB');
}

function ItemCard({ item, onPurchase, onEquip, onUnequip, busy, shekels }) {
  const Icon = Lucide[item.icon] || Lucide.Sparkles;
  const tier = TIER_RING[item.tier] || TIER_RING.common;
  const owned = !!item.owned;
  const equipped = !!item.equipped;
  const unlocked = !!item.unlocked;
  const canAfford = shekels >= item.price_shekels;

  let ctaLabel, ctaTestId, ctaAction, ctaDisabled, ctaTone;
  if (equipped) {
    ctaLabel = 'Equipped'; ctaTestId = `treasury-unequip-${item.slug}`;
    ctaAction = () => onUnequip(item); ctaDisabled = busy; ctaTone = 'ghost';
  } else if (owned) {
    ctaLabel = 'Equip'; ctaTestId = `treasury-equip-${item.slug}`;
    ctaAction = () => onEquip(item); ctaDisabled = busy; ctaTone = 'primary';
  } else if (!unlocked) {
    ctaLabel = item.keys_required > 0
      ? `${item.keys_required} keys`
      : `${item.streak_required}d streak`;
    ctaTestId = `treasury-locked-${item.slug}`;
    ctaAction = null; ctaDisabled = true; ctaTone = 'locked';
  } else if (!canAfford) {
    ctaLabel = `${formatShekels(item.price_shekels)}`; ctaTestId = `treasury-cantafford-${item.slug}`;
    ctaAction = null; ctaDisabled = true; ctaTone = 'locked';
  } else {
    ctaLabel = `Buy · ${formatShekels(item.price_shekels)}`;
    ctaTestId = `treasury-buy-${item.slug}`;
    ctaAction = () => onPurchase(item); ctaDisabled = busy; ctaTone = 'primary';
  }

  return (
    <motion.div
      whileHover={{ y: -3, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 380, damping: 24 }}
      className="codex-card"
      data-testid={`treasury-item-${item.slug}`}
      style={{
        position: 'relative',
        padding: 16,
        border: `1px solid ${unlocked ? tier.ring : 'rgba(245,217,124,0.1)'}`,
        boxShadow: equipped ? tier.glow : (unlocked ? 'inset 0 0 30px rgba(212,175,55,0.04)' : 'inset 0 0 30px rgba(0,0,0,0.4)'),
        background: equipped
          ? `radial-gradient(120% 80% at 50% 0%, ${item.accent}33 0%, transparent 70%), linear-gradient(180deg, rgba(245,217,124,0.06), rgba(168,85,247,0.04))`
          : 'linear-gradient(180deg, rgba(245,217,124,0.04), rgba(168,85,247,0.03))',
        opacity: unlocked ? 1 : 0.7,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      {/* Tier ribbon + Equipped flag */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontFamily: "'Cinzel', serif", fontSize: 9, letterSpacing: '0.25em',
          color: item.accent, padding: '3px 8px', borderRadius: 999,
          border: `1px solid ${item.accent}55`, background: `${item.accent}15`,
        }}>
          {item.tier.toUpperCase()}
        </span>
        {equipped && (
          <span style={{ fontSize: 10, fontFamily: "'Cinzel', serif", letterSpacing: '0.25em', color: 'var(--codex-gold-light)' }}>
            <Lucide.Check size={11} style={{ verticalAlign: '-1px' }} /> EQUIPPED
          </span>
        )}
        {owned && !equipped && (
          <span style={{ fontSize: 10, fontFamily: "'Cinzel', serif", letterSpacing: '0.25em', color: 'rgba(245,217,124,0.7)' }}>OWNED</span>
        )}
      </div>

      {/* Icon */}
      <div
        style={{
          width: 70, height: 70, borderRadius: '50%', margin: '4px auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: unlocked
            ? `radial-gradient(circle at 50% 35%, ${item.accent}aa, ${item.accent}33 60%, transparent 75%)`
            : 'radial-gradient(circle, rgba(120,120,140,0.2), transparent 70%)',
          border: `2px solid ${unlocked ? item.accent : 'rgba(120,120,140,0.3)'}`,
          filter: unlocked ? 'none' : 'grayscale(0.8) brightness(0.7)',
        }}
      >
        <Icon size={32} color={unlocked ? '#fff7d6' : '#a0a0b0'} strokeWidth={2} />
      </div>

      {/* Name + desc */}
      <div className="codex-display" style={{ textAlign: 'center', fontSize: 15, color: 'var(--codex-gold-light)', marginTop: 2 }}>
        {item.name}
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, opacity: 0.75, lineHeight: 1.4, minHeight: 30 }}>
        {item.description}
      </div>
      {item.lore && (
        <div style={{ textAlign: 'center', fontStyle: 'italic', fontSize: 10, color: 'rgba(245,217,124,0.55)', minHeight: 26 }}>
          "{item.lore}"
        </div>
      )}

      {/* CTA */}
      <button
        type="button"
        onClick={ctaAction || undefined}
        disabled={ctaDisabled}
        data-testid={ctaTestId}
        className="codex-btn"
        style={{
          width: '100%',
          padding: '9px 12px',
          fontSize: 12,
          letterSpacing: '0.08em',
          background: ctaTone === 'primary'
            ? 'linear-gradient(135deg, var(--codex-gold) 0%, var(--codex-orange) 100%)'
            : ctaTone === 'ghost'
              ? 'rgba(245,217,124,0.06)'
              : 'rgba(80,80,100,0.18)',
          color: ctaTone === 'primary' ? '#1a0e2c' : 'var(--codex-gold-light)',
          border: `1px solid ${ctaTone === 'primary' ? 'transparent' : 'rgba(245,217,124,0.18)'}`,
          opacity: ctaDisabled ? 0.55 : 1,
          cursor: ctaDisabled ? 'not-allowed' : 'pointer',
          marginTop: 'auto',
        }}
      >
        {ctaTone === 'locked' && <Lucide.Lock size={11} />}
        {ctaLabel}
      </button>
    </motion.div>
  );
}

export default function CodexTreasury() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activeSlot, setActiveSlot] = useState('all');

  const fetchAll = async () => {
    try {
      const cat = await codexApi.treasuryCatalog();
      setData(cat);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const sections = useMemo(() => {
    if (!data) return [];
    if (activeSlot === 'all') return data.sections;
    return data.sections.filter((s) => s.slot === activeSlot);
  }, [data, activeSlot]);

  const handlePurchase = async (item) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await codexApi.treasuryPurchase(item.id);
      celebrate(0.5);
      await fetchAll();
    } catch (e) {
      setError(e.message || 'Purchase failed');
    } finally { setBusy(false); }
  };

  const handleEquip = async (item) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await codexApi.treasuryEquip(item.id);
      await fetchAll();
    } catch (e) { setError(e.message || 'Equip failed'); } finally { setBusy(false); }
  };

  const handleUnequip = async (item) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await codexApi.treasuryUnequip(item.slot);
      await fetchAll();
    } catch (e) { setError(e.message || 'Unequip failed'); } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--codex-gold-light)' }} data-testid="treasury-loading">
        Opening the gates of the Treasury...
      </div>
    );
  }
  if (!data) return null;

  return (
    <div data-testid="codex-treasury-page">
      <div style={{ marginBottom: 22 }}>
        <div className="codex-eyebrow">SACRED TREASURY</div>
        <h1 className="codex-h1" style={{ marginBottom: 8 }}>The Inner Court</h1>
        <p style={{ color: 'rgba(253,246,227,0.78)', maxWidth: 640 }}>
          Trade your Shekels for sacred garments, symbols, and titles.
          Each item bears its lore — wear them not for vanity, but as remembrance.
        </p>
      </div>

      {/* Wallet hero */}
      <div className="codex-card-gold codex-card" style={{ padding: 22, marginBottom: 22, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }} data-testid="treasury-wallet">
        <div>
          <div className="codex-eyebrow" style={{ marginBottom: 4 }}>YOUR PURSE</div>
          <div className="codex-display" style={{ fontSize: 36, color: 'var(--codex-gold-light)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Lucide.Coins size={28} color="var(--codex-gold)" />
            <span data-testid="treasury-shekels">{formatShekels(data.shekels)}</span>
            <span style={{ fontSize: 13, opacity: 0.6, letterSpacing: '0.2em', marginLeft: 4 }}>SHEKELS</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, fontSize: 12, color: 'rgba(253,246,227,0.75)' }}>
          <div><span style={{ color: 'var(--codex-gold-light)' }}>{data.keys}</span> keys</div>
          <div><span style={{ color: 'var(--codex-gold-light)' }}>{data.streak_days}d</span> streak</div>
        </div>
      </div>

      {/* Slot tabs */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }} data-testid="treasury-tabs">
        {[{ key: 'all', label: 'All' }, ...Object.keys(SLOT_META).map((k) => ({ key: k, label: SLOT_META[k].label }))].map((t) => {
          const active = activeSlot === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveSlot(t.key)}
              data-testid={`treasury-tab-${t.key}`}
              className="codex-btn"
              style={{
                padding: '8px 14px',
                fontSize: 12,
                background: active ? 'rgba(212,175,55,0.18)' : 'rgba(245,217,124,0.04)',
                color: active ? 'var(--codex-gold-light)' : 'rgba(253,246,227,0.7)',
                borderColor: active ? 'rgba(212,175,55,0.5)' : 'rgba(245,217,124,0.15)',
                textTransform: 'none',
                letterSpacing: '0.08em',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid="treasury-error"
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              background: 'rgba(234,88,12,0.18)',
              border: '1px solid rgba(234,88,12,0.4)',
              color: '#ffb088',
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {sections.map((section) => {
        const meta = SLOT_META[section.slot] || { label: section.slot };
        const SectionIcon = Lucide[meta.icon] || Lucide.Sparkles;
        return (
          <div key={section.slot} style={{ marginBottom: 30 }} data-testid={`treasury-section-${section.slot}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <SectionIcon size={18} color="var(--codex-gold-light)" />
              <h2 className="codex-h2" style={{ color: 'var(--codex-gold-light)' }}>{meta.label}</h2>
            </div>
            {meta.blurb && <div style={{ fontSize: 12, color: 'rgba(253,246,227,0.6)', marginBottom: 14 }}>{meta.blurb}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {section.items.map((it) => (
                <ItemCard
                  key={it.id}
                  item={it}
                  shekels={data.shekels}
                  busy={busy}
                  onPurchase={handlePurchase}
                  onEquip={handleEquip}
                  onUnequip={handleUnequip}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
